import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Button, Card, CardBody, CardFooter, CardHeader, Col,
  Form, FormFeedback, FormGroup, FormText, Input, Label, Row,
} from 'reactstrap';
import isEmail from 'validator/es/lib/isEmail';
import isUUID from 'validator/es/lib/isUUID';

import LinkInI18nBlock from 'src/enosikit/components/LinkInI18nBlock';
import FlashesStore from 'src/stores/FlashesStore';
import {
  AllDaysOfWeek, AllMonthsOfYear, AllTimesOfDay,
  TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL, TRADE_DIRECTION_UNSPECIFIED,
} from 'src/util/constants';
import { convertEnergyPricePartnerToPowertracer, convertEnergyPricePowertracerToPartner } from 'src/util/conversions';
import { useIntlCurrency } from 'src/util/i18n/currency';
import { getTradePointLabel } from 'src/util/i18n/tradeRule';
import isActive from 'src/util/isActive';
import { isFloatValue } from 'src/util/math';
import isDirectionValid from 'src/util/tradeDirection/validation';
import { isProposerTradePointIdValid } from 'src/util/tradeRule/validation';

const FlatPricingFormFragment = graphql`
  fragment TradeRuleProposeNominatedFlatPricingForm_property on Property {
    uuid
    title
    timezone
    publicHolidayRegion
    meters {
      edges {
        node {
          uuid
          identifier
          title
          tradePointId
          active { start finish }
        }
      }
    }
  }
`;

/**
 * FlatPricingForm component for proposing nominated flat-rate trades
 * @param {object} props - Component props
 * @param {Function} props.onPricingButtonClick function handling the change of
 * state between the time of use pricing and flat pricing forms.
 * @param {Function} props.handleTradeRuleProposeNominated - Mutation handler
 * @param {object} props.property - Property data from GraphQL
 * @param {boolean} props.processing - Whether mutation is processing
 * @param {object} props.router - Router object for navigation
 * @returns {React.ReactElement} - FlatPricingForm component
 */
function FlatPricingForm(props) {
  let { property } = props;
  const {
    onPricingButtonClick, handleTradeRuleProposeNominated, processing, router,
  } = props;

  const intl = useIntl();

  property = useFragment(FlatPricingFormFragment, property);

  const [direction, setDirection] = useState(TRADE_DIRECTION_UNSPECIFIED);
  const [price, setPrice] = useState('');
  const [proposerTradePointId, setProposerTradePointId] = useState(null);
  const [recipientUserEmail, setRecipientUserEmail] = useState('');
  const [directionValid, setDirectionValid] = useState(null);
  const [priceValid, setPriceValid] = useState(null);
  const [proposerTradePointIdValid, setProposerTradePointIdValid] = useState(null);
  const [recipientUserEmailValid, setRecipientUserEmailValid] = useState(null);

  const validateDirection = () => {
    const directionValidResult = isDirectionValid(direction);
    setDirectionValid(directionValidResult);
  };

  const validatePrice = () => {
    const priceValidResult = isFloatValue(price);
    setPriceValid(priceValidResult);
  };

  const validateProposerTradePointId = () => {
    const proposerTradePointIdValidResult = isProposerTradePointIdValid(proposerTradePointId);
    setProposerTradePointIdValid(proposerTradePointIdValidResult);
  };

  const validateRecipientUserEmail = () => {
    const recipientUserEmailValidResult = isEmail(recipientUserEmail);
    setRecipientUserEmailValid(recipientUserEmailValidResult);
  };

  const validateForm = () => {
    validateDirection();
    validatePrice();
    validateProposerTradePointId();
    validateRecipientUserEmail();
  };

  const isValid = () => priceValid !== null && priceValid
      && directionValid !== null && directionValid
      && proposerTradePointIdValid !== null && proposerTradePointIdValid
      && recipientUserEmailValid !== null && recipientUserEmailValid;

  const handleSubmit = (event) => {
    event.preventDefault();

    const { timezone, publicHolidayRegion } = property;

    const processingMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.state.invalid_still_processing', defaultMessage: 'We are still processing your request...' });

    const errorMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.state.invalid_data', defaultMessage: 'Form data not valid. Please see below.' });
    const mutationName = 'proposeNominatedTrade';

    if (processing) {
      FlashesStore.flash(FlashesStore.INFO, processingMsg, mutationName);
      return;
    }

    validateForm();

    if (!isValid()) {
      FlashesStore.flash(FlashesStore.ERROR, errorMsg, mutationName);
      return;
    }

    const input = {
      direction,
      clauses: [
        {
          price,
          timezone,
          publicHolidayRegion,
          ignoreDaylightSavings: false,
          ignorePublicHolidays: false,
          monthsOfYear: AllMonthsOfYear,
          daysOfWeek: AllDaysOfWeek,
          timesOfDay: [AllTimesOfDay],
        },
      ],
      proposerTradePointId,
      recipientUserEmail,
    };

    handleTradeRuleProposeNominated(input);
  };

  const handleDirectionChange = (event) => {
    const { value: directionValue } = event.target;
    const directionValidResult = isDirectionValid(directionValue);

    setDirection(directionValue);
    setDirectionValid(directionValidResult);
  };

  const handlePriceChange = (event) => {
    const { value: updatedPrice } = event.target;
    const intlCurrency = useIntlCurrency();
    const updatedPriceBig = convertEnergyPricePartnerToPowertracer(
      intlCurrency,
      parseFloat(updatedPrice),
    );

    setPrice(updatedPriceBig);
    setPriceValid(isFloatValue(updatedPriceBig));
  };

  const handleRecipientUserEmailChange = (event) => {
    const { value: recipientUserEmailValue } = event.target;
    const recipientUserEmailValidResult = isEmail(recipientUserEmailValue);

    setRecipientUserEmail(recipientUserEmailValue);
    setRecipientUserEmailValid(recipientUserEmailValidResult);
  };

  const handleProposerTradePointIdChange = (event) => {
    const { value: proposerTradePointIdValue } = event.target;
    const proposerTradePointIdValidResult = isUUID(proposerTradePointIdValue);

    setProposerTradePointId(proposerTradePointIdValue);
    setProposerTradePointIdValid(proposerTradePointIdValidResult);
  };

  const proposePeerToPeerTradeHeading = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.propose_peer_to_peer_trade.heading.label',
    defaultMessage: 'Propose a peer-to-peer trade',
  });
  const timeOfUsePricingLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.time_of_use_pricing.label',
    defaultMessage: 'Time-of-use pricing',
  });
  const directionLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.direction.label',
    defaultMessage: 'Are you buying or selling?',
  });
  const directionPlaceholder = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.direction.placeholder',
    defaultMessage: "Select 'Buy' or 'Sell'",
  });
  const buyText = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.direction.trade_direction_buy.text',
    defaultMessage: 'Buy',
  });
  const sellText = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.direction.trade_direction_sell.text',
    defaultMessage: 'Sell',
  });
  const invalidDirectionFeedback = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.direction.invalid_direction.feedback',
    defaultMessage: 'Invalid direction',
  });
  const directionHint = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.direction.hint',
    defaultMessage: 'Are you proposing to sell your energy, or buy energy from a friend?',
  });

  const { meters } = property;
  const meterNodes = meters && meters.edges.map((edge) => (edge.node));

  const priceLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.price.label',
    defaultMessage: 'Price',
  });
  const invalidPriceFeedback = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.price.invalid_price.feedback',
    defaultMessage: 'Invalid price',
  });
  const priceHint = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.price.hint',
    defaultMessage: 'What price, in c/kWh, do you want to propose?',
  });
  const selectTradePointLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.proposer_trade_point_id.default.label',
    defaultMessage: 'Select a Trade Point',
  });
  const invalidTradePointFeedback = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.proposer_trade_point_id.invalid_trade_point.feedback',
    defaultMessage: 'Invalid trade point selection',
  });
  const tradePointHint = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.proposer_trade_point_id.hint',
    defaultMessage: 'Which trade point are you using?',
  });
  const recipientEmailLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.recipient_user_email.label',
    defaultMessage: 'Recipient email',
  });
  const invalidEmailFeedback = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.recipient_user_email.invalid_email.feedback',
    defaultMessage: 'Invalid email',
  });
  const proposeLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.submit.label',
    defaultMessage: 'Propose',
  });
  const cancelLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_propose_nominated.flat_pricing_form.cancel.label',
    defaultMessage: 'Cancel',
  });

  const tradePointLabel = getTradePointLabel(intl, direction || TRADE_DIRECTION_UNSPECIFIED);
  const intlCurrency = useIntlCurrency();

  return (
    <Form onSubmit={handleSubmit} disabled={processing}>
      <Card>
        <CardHeader className="d-flex flex-wrap">
          <h2 className="mb-0">
            {proposePeerToPeerTradeHeading}
          </h2>
          <Button color="darken" className="ms-auto" onClick={onPricingButtonClick}>
            {timeOfUsePricingLabel}
          </Button>
        </CardHeader>
        <CardBody>
          <FormGroup>
            <Label for="direction">{directionLabel}</Label>
            <Input type="select" name="direction" id="direction" defaultValue={direction} onChange={handleDirectionChange} disabled={processing} valid={directionValid} invalid={directionValid !== null && !directionValid}>
              <option value="">
                {directionPlaceholder}
              </option>
              <option value={TRADE_DIRECTION_BUY}>
                {buyText}
              </option>
              <option value={TRADE_DIRECTION_SELL}>
                {sellText}
              </option>
            </Input>
            <FormFeedback>{invalidDirectionFeedback}</FormFeedback>
            <FormText>
              {directionHint}
            </FormText>
          </FormGroup>
          <FormGroup>
            <Label for="price">{priceLabel}</Label>
            <Input type="number" name="price" id="price" value={convertEnergyPricePowertracerToPartner(intlCurrency, price)} onChange={handlePriceChange} disabled={processing} valid={priceValid} invalid={priceValid !== null && !priceValid} />
            <FormFeedback>{invalidPriceFeedback}</FormFeedback>
            <FormText>
              {priceHint}
            </FormText>
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="proposerTradePointId">{tradePointLabel}</Label>
                <Input type="select" name="select" id="proposerTradePointId" defaultValue={proposerTradePointId} onChange={handleProposerTradePointIdChange} disabled={processing} valid={proposerTradePointIdValid} invalid={proposerTradePointIdValid !== null && !proposerTradePointIdValid}>
                  <option key="meter-id-null" value="">{selectTradePointLabel}</option>
                  {meterNodes && meterNodes.map((meter) => (
                    <option
                      value={meter.tradePointId}
                      disabled={!isActive(meter.active)}
                      key={meter.uuid}
                    >
                      {`${meter.title} (${meter.identifier})`}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{invalidTradePointFeedback}</FormFeedback>
                <FormText>{tradePointHint}</FormText>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="recipientUserEmail">{recipientEmailLabel}</Label>
                <Input type="email" name="recipientUserEmail" id="recipientUserEmail" value={recipientUserEmail} onChange={handleRecipientUserEmailChange} disabled={processing} valid={recipientUserEmailValid} invalid={recipientUserEmailValid !== null && !recipientUserEmailValid} />
                <FormFeedback>{invalidEmailFeedback}</FormFeedback>
                <FormText>
                  {/* FormattedMessage required (not intl.formatMessage) for JSX component interpolation. See: https://formatjs.github.io/docs/react-intl/components/#rich-text-formatting */}
                  {/* intl.formatMessage() returns strings only, renders React components as [object Object]. See: https://formatjs.github.io/docs/react-intl/api/#usage */}
                  <FormattedMessage
                    id="trade_rule.trade_rule_propose_nominated.trade_rule_propose_nominated_flat_pricing_form.recipient_user_email.hint.text"
                    defaultMessage="Who do you want to propose to trade with?"
                    values={{
                      link: (chunks) => (
                        <LinkInI18nBlock
                          url={intl.formatMessage({ id: 'trade_rule.trade_rule_propose_nominated.trade_rule_propose_nominated_flat_pricing_form.recipient_user_email.hint.url', defaultMessage: 'trade_rule.trade_rule_propose_nominated.trade_rule_propose_nominated_flat_pricing_form.recipient_user_email.hint.url' })}
                          urlKey="trade_rule.trade_rule_propose_nominated.trade_rule_propose_nominated_flat_pricing_form.recipient_user_email.hint.url"
                          chunks={chunks}
                        />
                      ),
                    }}
                  />
                </FormText>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
        <CardFooter>
          <Button disabled={processing}>{proposeLabel}</Button>
          <Button color="" onClick={() => (router.go(-1))} disabled={processing}>
            {cancelLabel}
          </Button>
        </CardFooter>
      </Card>
    </Form>
  );
}

FlatPricingForm.propTypes = {
  handleTradeRuleProposeNominated: PropTypes.func,
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  processing: PropTypes.bool,
  router: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  onPricingButtonClick: PropTypes.func.isRequired,
};

FlatPricingForm.defaultProps = {
  handleTradeRuleProposeNominated: null,
  property: null,
  processing: false,
};

export default FlatPricingForm;
